import UmlSeqParticipantShape from '../Types/UmlSeqParticipantShape';
import UmlSeqLifelineShape from '../Types/UmlSeqLifelineShape';
import UmlSeqActivationShape from '../Types/UmlSeqActivationShape';
import UmlSeqMessageShape from '../Types/UmlSeqMessageShape';

/**
 * Sequence-diagram shapes are linked by a shared TOP-participant id
 * (lifeline.participantId, activation.participantId, message.fromParticipantId/
 * toParticipantId, and the bottom duplicate box's linkedParticipantId all
 * point at the same top UmlSeqParticipantShape) rather than a real
 * UmlCompositeShape parent/child tree — the generic package-containment
 * model (escape-if-moved-outside-bounds, see UmlCompositeShape.containsShape)
 * doesn't fit here: a lifeline is mostly OUTSIDE its participant's small box
 * by design, so bounding-box containment would mark it "escaped" on the
 * very first drag. This is a dedicated, purpose-built cascade instead,
 * called from UmlCanvasEngine.updateShapePosition/translateChildren for
 * every actual shape move (direct drag OR moved as someone else's
 * package-child) so these shapes are REALLY kept in sync, not just
 * re-derived live at render time (that was the 2026-07-19 "shapes aren't
 * connected" bug — lifeline/activation/message widgets each did their own
 * getShapeById lookup, and the bottom box didn't track at all).
 *
 * Rules:
 * - Moving the TOP participant (horizontal drag, or dragged along as a
 *   package's child) cascades dx to its lifeline, that lifeline's
 *   activation bar(s), any message touching this participant (fromX/toX +
 *   recomputed x/w), and the BOTTOM duplicate participant (x only — the
 *   bottom box's own y is its own deliberate vertical position, see below).
 * - Moving the BOTTOM duplicate participant resizes its lifeline's height
 *   to keep spanning down to the new bottom position (dy only — see
 *   UmlCanvasEngine.updateShapePosition, which locks the bottom box's x so
 *   it can only ever move vertically, never desync sideways from the lifeline).
 * @param {object[]} allShapes - umlCanvasEngine.shapes (top-level; packages nest their own children, walked separately by findAllSeqShapes)
 * @param {object} movedShape - the shape that just moved
 * @param {number} dx
 * @param {number} dy
 */
export function cascadeSeqDiaMove(allShapes, movedShape, dx, dy) {
  if (!(movedShape instanceof UmlSeqParticipantShape)) {
    return;
  }
  if (movedShape.linkedParticipantId) {
    cascadeFromBottomParticipant(allShapes, movedShape, dy);
  } else if (dx !== 0) {
    cascadeFromTopParticipant(allShapes, movedShape, dx);
  }
}

/**
 * Flat search across top-level shapes only — lifelines/activations/messages
 * are always added as TOP-LEVEL shapes (see CsSeqCanvasLayoutBuilder/
 * addSequenceLayoutToCanvas), never nested inside a package, so no need for
 * findNestedShape's recursion here. Only UmlSeqParticipantShape itself lives
 * inside a package's children — callers already have that instance directly
 * (it's the movedShape/bottomTwin), never need to search FOR one here.
 * @param {object[]} allShapes
 * @returns {object[]}
 */
function findAllSeqShapes(allShapes) {
  return allShapes.filter(function isSeqShape(s) {
    return s instanceof UmlSeqLifelineShape || s instanceof UmlSeqActivationShape || s instanceof UmlSeqMessageShape || s instanceof UmlSeqParticipantShape;
  });
}

function cascadeFromTopParticipant(allShapes, topParticipant, dx) {
  const seqShapes = findAllSeqShapes(allShapes);

  seqShapes.forEach(function moveRelated(s) {
    if ((s instanceof UmlSeqLifelineShape || s instanceof UmlSeqActivationShape) && s.participantId === topParticipant.id) {
      s.moveTo(s.x + dx, s.y);
    } else if (s instanceof UmlSeqParticipantShape && s.linkedParticipantId === topParticipant.id) {
      s.moveTo(s.x + dx, s.y); // bottom twin: x follows, y is its own
    } else if (s instanceof UmlSeqMessageShape && (s.fromParticipantId === topParticipant.id || s.toParticipantId === topParticipant.id)) {
      if (s.fromParticipantId === topParticipant.id) s.fromX += dx;
      if (s.toParticipantId === topParticipant.id) s.toX += dx;
      s.x = Math.min(s.fromX, s.toX);
      s.w = Math.abs(s.toX - s.fromX) || 1;
    }
  });
}

function cascadeFromBottomParticipant(allShapes, bottomParticipant, dy) {
  if (dy === 0) return;
  const lifeline = findAllSeqShapes(allShapes).find(function isOwnLifeline(s) {
    return s instanceof UmlSeqLifelineShape && s.participantId === bottomParticipant.linkedParticipantId;
  });
  if (lifeline) {
    lifeline.resizeTo(lifeline.w, lifeline.h + dy); // top stays put, bottom edge follows the twin
  }
}

/**
 * Row-range [lo, hi] a message/activation's OWN call/return spans — shared
 * by clampSeqMessageY and the nested-group-move below.
 * @param {object[]} allShapes
 * @param {object} shape - UmlSeqMessageShape or UmlSeqActivationShape
 * @returns {[number, number]|null} null if a message has no paired return/call yet
 */
function getOwnRowRange(allShapes, shape) {
  if (shape instanceof UmlSeqActivationShape) {
    return [shape.startRowIndex, shape.endRowIndex];
  }
  if (shape instanceof UmlSeqMessageShape) {
    const paired = shape.pairedMessageId ? allShapes.find(function isPaired(s) { return s.id === shape.pairedMessageId; }) : null;
    if (!paired) return [shape.rowIndex, shape.rowIndex];
    return [Math.min(shape.rowIndex, paired.rowIndex), Math.max(shape.rowIndex, paired.rowIndex)];
  }
  return null;
}

/**
 * Nested group-move: everything that happens temporally INSIDE the dragged
 * message/activation's own call→return span must shift by the same dy, or
 * it visually detaches from the thing that (from the model's perspective)
 * is still its parent call. A message strictly BETWEEN [lo, hi] (exclusive
 * — the boundary rows belong to the dragged item itself, not its nested
 * content) and any activation fully CONTAINED within [lo, hi] both qualify.
 * Doesn't touch x/width — only the vertical shift cascades; horizontal sync
 * is still cascadeSeqDiaMove's job (participant drags).
 * @param {object[]} allShapes
 * @param {object} draggedShape - the UmlSeqMessageShape or UmlSeqActivationShape just dragged
 * @param {number} dy
 */
export function cascadeNestedSeqDiaShift(allShapes, draggedShape, dy) {
  if (dy === 0) return;
  const range = getOwnRowRange(allShapes, draggedShape);
  if (!range) return;
  const [lo, hi] = range;

  allShapes.forEach(function shiftIfNested(s) {
    if (s === draggedShape) return;
    if (s instanceof UmlSeqMessageShape && lo < s.rowIndex && s.rowIndex < hi) {
      s.moveTo(s.x, s.y + dy);
    } else if (s instanceof UmlSeqActivationShape && lo <= s.startRowIndex && s.endRowIndex <= hi) {
      s.moveTo(s.x, s.y + dy);
    }
  });
}

/**
 * Clamps a UmlSeqMessageShape's proposed new y to two constraints:
 * 1. Call/return ordering — a call can never sink below (>=) its own
 *    return, and a return can never rise above (<=) its own call
 *    (pairedMessageId, see CsSeqCanvasLayoutBuilder.pairMessagesWithReturns).
 * 2. Activation bounds — stays within whichever activation bar(s) belong to
 *    the participants this message touches (intersection of both, if it
 *    touches two active bars at once).
 * @param {object[]} allShapes
 * @param {object} message - the UmlSeqMessageShape being dragged
 * @param {number} proposedY
 * @returns {number} clamped y
 */
export function clampSeqMessageY(allShapes, message, proposedY) {
  let minY = -Infinity;
  let maxY = Infinity;

  if (message.pairedMessageId) {
    const paired = allShapes.find(function isPaired(s) { return s.id === message.pairedMessageId; });
    if (paired) {
      if (message.isReturn) {
        minY = Math.max(minY, paired.y);
      } else {
        maxY = Math.min(maxY, paired.y);
      }
    }
  }

  allShapes.forEach(function clampToActivation(s) {
    if (!(s instanceof UmlSeqActivationShape)) return;
    if (s.participantId !== message.fromParticipantId && s.participantId !== message.toParticipantId) return;
    minY = Math.max(minY, s.y);
    maxY = Math.min(maxY, s.y + s.h);
  });

  return Math.min(Math.max(proposedY, minY), maxY);
}
